import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type AiReportType = 'daily' | 'boq_summary' | 'bom_summary' | 'deviation';

export const AI_REPORT_TYPES: { type: AiReportType; label: string; description: string }[] = [
  { type: 'daily', label: 'Daily report', description: 'Labour, work logs, quantities, weather, safety and hindrances for one day.' },
  { type: 'boq_summary', label: 'BoQ summary', description: 'Value concentration, cost mix, markups, budget fit and data issues in a BoQ.' },
  { type: 'bom_summary', label: 'BoM summary', description: 'Material value by category, top materials, rate checks and BoQ alignment.' },
  { type: 'deviation', label: 'Deviation report', description: 'Schedule, labour, quantity and logged stage deviations for a period.' },
];

export interface AiReportParams {
  type: AiReportType;
  projectId: number;
  date?: string | null;
  boqId?: number | null;
  bomId?: number | null;
  from?: string | null;
  to?: string | null;
  useAi?: boolean;
}

export interface AiReportKpi {
  key: string;
  label: string;
  value: number | string | null;
  unit: string | null;
}

export interface AiReportColumn {
  key: string;
  label: string;
  numeric?: boolean;
}

export interface AiReportTable {
  key: string;
  title: string;
  columns: AiReportColumn[];
  rows: Record<string, any>[];
  total_rows: number;
  truncated: boolean;
}

export interface AiReportFlag {
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

export interface AiReportFacts {
  type: AiReportType;
  project: { id: number; name: string; code: string | null; org_id: number | null };
  period: Record<string, any>;
  summary: Record<string, any>;
  kpis: AiReportKpi[];
  tables: AiReportTable[];
  flags: AiReportFlag[];
  generated_at: string;
}

export interface AiReportNarrative {
  headline: string;
  summary: string;
  highlights: string[];
  risks: { severity: 'critical' | 'warning' | 'info'; text: string }[];
  recommendations: string[];
  data_gaps: string[];
  ai_note?: string;
}

export interface AiReport {
  id: number;
  org_id: number | null;
  project_id: number;
  report_type: AiReportType;
  title: string;
  params: Record<string, any>;
  facts: AiReportFacts;
  narrative: AiReportNarrative;
  ai_used: boolean;
  model: string | null;
  status: string;
  created_by: number | null;
  created_at: string;
}

export interface AiReportListItem {
  id: number;
  project_id: number;
  project_name: string | null;
  report_type: AiReportType;
  title: string;
  params: Record<string, any>;
  ai_used: boolean;
  model: string | null;
  headline: string | null;
  created_at: string;
}

@Injectable({ providedIn: 'root' })
export class AiReportsService {
  private baseUrl = `${environment.BASE_URL}ai-reports`;

  constructor(private http: HttpClient) {}

  generate(params: AiReportParams): Observable<AiReport> {
    return this.http
      .post<{ status: string; data: AiReport }>(`${this.baseUrl}/generate`, params)
      .pipe(map((res) => res.data));
  }

  list(filter: { projectId?: number | null; type?: AiReportType | null; limit?: number } = {}): Observable<AiReportListItem[]> {
    let params = new HttpParams();
    if (filter.projectId) params = params.set('projectId', String(filter.projectId));
    if (filter.type) params = params.set('type', filter.type);
    if (filter.limit) params = params.set('limit', String(filter.limit));
    return this.http
      .get<{ status: string; data: AiReportListItem[] }>(this.baseUrl, { params })
      .pipe(map((res) => res.data || []));
  }

  get(id: number): Observable<AiReport> {
    return this.http
      .get<{ status: string; data: AiReport }>(`${this.baseUrl}/${id}`)
      .pipe(map((res) => res.data));
  }

  remove(id: number): Observable<void> {
    return this.http.delete<unknown>(`${this.baseUrl}/${id}`).pipe(map(() => undefined));
  }

  /** Rebuilds the generate payload from a saved report so it can be regenerated. */
  paramsFromReport(report: AiReport): AiReportParams {
    const p = report.params || {};
    return {
      type: report.report_type,
      projectId: report.project_id,
      date: p['date'] ?? null,
      boqId: p['boqId'] ?? p['boq_id'] ?? null,
      bomId: p['bomId'] ?? p['bom_id'] ?? null,
      from: p['from'] ?? null,
      to: p['to'] ?? null,
    };
  }
}
